import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Users, Shield, AlertCircle, TrendingUp, Award,
  Search, Filter, Eye, CheckCircle, XCircle, Clock,
  Video, Store, Ban, AlertTriangle, Star, FileText
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import {
  getAllMonitoringData,
  getAllReports,
  reviewReport,
  reactivateAccount,
} from "../../redux/Actions/ProfessionalMonitoringActions";
import { RootState, AppDispatch } from "../../redux/store";

const ProfessionalMonitoring = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { allMonitoringData, monitoringPagination, reports, reportsPagination, loading } = useSelector(
    (state: RootState) => state.professionalMonitoring
  );
  
  const [activeTab, setActiveTab] = useState<"monitoring" | "reports">("monitoring");
  const [filterTier, setFilterTier] = useState<string>("all");
  const [filterSuspended, setFilterSuspended] = useState<boolean | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  
  // Pagination states
  const [monitoringPage, setMonitoringPage] = useState(1);
  const [reportsPage, setReportsPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch monitoring data with pagination
  const fetchMonitoringData = () => {
    const filters: any = { page: monitoringPage, limit: itemsPerPage };
    if (filterTier !== "all") filters.performanceTier = filterTier;
    if (filterSuspended !== undefined) filters.isSuspended = filterSuspended;
    dispatch(getAllMonitoringData(filters));
  };

  // Fetch reports with pagination
  const fetchReportsData = () => {
    dispatch(getAllReports({ page: reportsPage, limit: itemsPerPage }));
  };

  useEffect(() => {
    fetchMonitoringData();
  }, [monitoringPage, filterTier, filterSuspended]);

  useEffect(() => {
    fetchReportsData();
  }, [reportsPage]);

  const handleReviewReportComplete = async (reportId: string, status: string, adminNotes: string) => {
    const user = (window as any).user || { id: "admin" };
    await dispatch(reviewReport({
      reportId,
      status,
      adminNotes,
      reviewedBy: user.id
    }));
    setShowReviewModal(false);
    // Refresh current page of reports
    fetchReportsData();
  };

  const handleReactivateAccount = async (userId: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to reactivate this professional's account?\n\n" +
      "This will:\n" +
      "• Remove suspension\n" +
      "• Reset home visits to 0\n" +
      "• Reset warnings to 0\n" +
      "• Reset anomalies to 0\n" +
      "• Dismiss all active warnings\n\n" +
      "Do you want to continue?"
    );

    if (!confirmed) return;

    try {
      await dispatch(reactivateAccount(userId)).unwrap();
      // Refresh the monitoring data after successful reactivation
      fetchMonitoringData();
    } catch (error) {
      // Error is already handled by the action with toast
      console.error("Failed to reactivate account:", error);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "Elite": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Advanced": return "bg-green-100 text-green-800 border-green-200";
      case "Standard": return "bg-blue-100 text-blue-800 border-blue-200";
      case "Basic": return "bg-gray-100 text-gray-800 border-gray-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (loading && !allMonitoringData.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Professional Monitoring</h1>
                <p className="text-gray-600 mt-1">Monitor professional performance and violations</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 border-b">
              <button
                onClick={() => setActiveTab("monitoring")}
                className={`pb-3 px-4 font-medium transition-colors ${
                  activeTab === "monitoring"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Users className="h-4 w-4 inline mr-2" />
                Monitoring Data
              </button>
              <button
                onClick={() => setActiveTab("reports")}
                className={`pb-3 px-4 font-medium transition-colors ${
                  activeTab === "reports"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <FileText className="h-4 w-4 inline mr-2" />
                Client Reports ({reports.length})
              </button>
            </div>

            {/* Monitoring Data Tab */}
            {activeTab === "monitoring" && (
              <>
                {/* Filters */}
                <Card className="p-4">
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Performance Tier</label>
                      <select
                        value={filterTier}
                        onChange={(e) => setFilterTier(e.target.value)}
                        className="px-3 py-2 border rounded-lg"
                      >
                        <option value="all">All Tiers</option>
                        <option value="Elite">Elite</option>
                        <option value="Advanced">Advanced</option>
                        <option value="Standard">Standard</option>
                        <option value="Basic">Basic</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Status</label>
                      <select
                        value={filterSuspended === undefined ? "all" : filterSuspended ? "suspended" : "active"}
                        onChange={(e) => setFilterSuspended(
                          e.target.value === "all" ? undefined : e.target.value === "suspended"
                        )}
                        className="px-3 py-2 border rounded-lg"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                  </div>
                </Card>

                {/* Monitoring Cards */}
                <div className="grid grid-cols-1 gap-4">
                  {allMonitoringData.map((monitoring) => (
                    <Card key={monitoring.id} className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            monitoring.professionalType === "home_service" 
                              ? "bg-blue-100" 
                              : "bg-purple-100"
                          }`}>
                            {monitoring.professionalType === "home_service" ? (
                              <Store className="h-6 w-6 text-blue-600" />
                            ) : (
                              <Video className="h-6 w-6 text-purple-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">
                              {(() => {
                                const user: any = monitoring.userId;
                                if (user?.firstName || user?.lastName) {
                                  return `${user.firstName || ""} ${user.lastName || ""}`.trim();
                                }
                                return user?.email || `ID: ${String(user?._id || user || "").substring(0, 8)}...`;
                              })()}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {monitoring.professionalType === "home_service" ? "Home Service" : "Remote/Video"}
                              {(() => {
                                const user: any = monitoring.userId;
                                return user?.email ? ` • ${user.email}` : "";
                              })()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant={undefined} className={`${getTierColor(monitoring.performanceTier)} border`}>
                            <Award className="h-3 w-3 mr-1" />
                            {monitoring.performanceTier}
                          </Badge>
                          {monitoring.isSuspended && (
                            <Badge variant={undefined} className="bg-red-100 text-red-800 border-red-200">
                              <Ban className="h-3 w-3 mr-1" />
                              Suspended
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Trust Score</p>
                          <p className="text-xl font-bold text-gray-900">{monitoring.trustScore}</p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">
                            {monitoring.professionalType === "home_service" ? "Home Visits" : "Video Calls"}
                          </p>
                          <p className="text-xl font-bold text-blue-900">
                            {monitoring.professionalType === "home_service" 
                              ? monitoring.totalHomeVisits 
                              : monitoring.totalVideoCalls}
                          </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Contracts Closed</p>
                          <p className="text-xl font-bold text-green-900">
                            {monitoring.professionalType === "home_service" 
                              ? monitoring.contractsClosedInPeriod 
                              : monitoring.videoContractsClosedInPeriod}
                          </p>
                        </div>
                        <div className="p-3 bg-yellow-50 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Warnings</p>
                          <p className="text-xl font-bold text-yellow-900">{monitoring.totalWarnings}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">
                            Reports: <strong>{monitoring.confirmedReports}</strong>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">
                            Compliant Months: <strong>{monitoring.compliantMonthsInRow}</strong>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">
                            Anomalies: <strong>{monitoring.anomalyCount}</strong>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-purple-500" />
                          <span className="text-gray-600">
                            Refund Requests: <strong>{monitoring.totalRefundRequests || 0}</strong>
                          </span>
                        </div>
                      </div>

                      {monitoring.isSuspended && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 flex-1">
                              <Ban className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                              <div className="text-sm">
                                <p className="font-medium text-red-900">Suspended</p>
                                <p className="text-red-700">{monitoring.suspensionReason}</p>
                                {monitoring.suspensionEndDate && !monitoring.isPermanentlyBanned && (
                                  <p className="text-red-600 mt-1">
                                    Ends: {new Date(monitoring.suspensionEndDate).toLocaleDateString()}
                                  </p>
                                )}
                                {monitoring.isPermanentlyBanned && (
                                  <p className="text-red-600 mt-1 font-medium">
                                    Permanent Suspension
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const userIdValue = typeof monitoring.userId === "object" && monitoring.userId !== null
                                  ? (monitoring.userId as any).id || (monitoring.userId as any)._id
                                  : monitoring.userId;
                                handleReactivateAccount(userIdValue);
                              }}
                              className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100 flex-shrink-0"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Reactivate
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>

                {allMonitoringData.length === 0 && (
                  <Card className="p-12">
                    <div className="text-center">
                      <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Monitoring Data</h3>
                      <p className="text-gray-600">
                        No professional monitoring data found with the selected filters.
                      </p>
                    </div>
                  </Card>
                )}

                {/* Monitoring Pagination */}
                {monitoringPagination && (
                  <Card className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Showing {((monitoringPagination.currentPage - 1) * itemsPerPage) + 1} - {Math.min(monitoringPagination.currentPage * itemsPerPage, monitoringPagination.totalCount)} of {monitoringPagination.totalCount} professionals
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMonitoringPage(prev => prev - 1)}
                          disabled={!monitoringPagination.hasPreviousPage}
                        >
                          Previous
                        </Button>
                        <div className="text-sm text-gray-600">
                          Page {monitoringPagination.currentPage} of {monitoringPagination.totalPages}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMonitoringPage(prev => prev + 1)}
                          disabled={!monitoringPagination.hasNextPage}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}
              </>
            )}

            {/* Reports Tab */}
            {activeTab === "reports" && (
              <>
                <div className="grid grid-cols-1 gap-4">
                  {reports.map((report) => (
                    <Card key={report.id} className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-5 w-5 text-orange-600" />
                            <h3 className="font-semibold text-lg capitalize">
                              {report.reportType.replace(/_/g, " ")}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Professional:</span>{" "}
                            {(() => {
                              const professional: any = report.professionalId;
                              if (professional?.firstName || professional?.lastName) {
                                return `${professional.firstName || ""} ${professional.lastName || ""}`.trim();
                              }
                              return professional?.email || `ID: ${String(professional?._id || professional || "").substring(0, 12)}...`;
                            })()}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Client:</span>{" "}
                            {(() => {
                              const client: any = report.clientId;
                              if (client?.firstName || client?.lastName) {
                                return `${client.firstName || ""} ${client.lastName || ""}`.trim();
                              }
                              return client?.email || `ID: ${String(client?._id || client || "").substring(0, 12)}...`;
                            })()}
                          </p>
                        </div>
                        
                        <Badge variant={undefined} className={
                          report.status === "confirmed" ? "bg-red-100 text-red-800" :
                          report.status === "rejected" ? "bg-gray-100 text-gray-800" :
                          report.status === "investigating" ? "bg-blue-100 text-blue-800" :
                          "bg-yellow-100 text-yellow-800"
                        }>
                          {report.status}
                        </Badge>
                      </div>

                      <div className="mb-4">
                        <p className="text-gray-700">{report.description}</p>
                      </div>

                      {report.adminNotes && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-medium text-blue-900 mb-1">Admin Notes:</p>
                          <p className="text-sm text-blue-800">{report.adminNotes}</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>Submitted: {new Date(report.createdAt).toLocaleString()}</span>
                        {report.status === "pending" && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedReport(report);
                                setShowReviewModal(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                {reports.length === 0 && (
                  <Card className="p-12">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reports</h3>
                      <p className="text-gray-600">
                        No customer reports have been submitted yet.
                      </p>
                    </div>
                  </Card>
                )}

                {/* Reports Pagination */}
                {reportsPagination && reportsPagination.totalPages > 1 && (
                  <Card className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Showing {((reportsPagination.currentPage - 1) * itemsPerPage) + 1} - {Math.min(reportsPagination.currentPage * itemsPerPage, reportsPagination.totalCount)} of {reportsPagination.totalCount} reports
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReportsPage(prev => prev - 1)}
                          disabled={!reportsPagination.hasPreviousPage}
                        >
                          Previous
                        </Button>
                        <div className="text-sm text-gray-600">
                          Page {reportsPagination.currentPage} of {reportsPagination.totalPages}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setReportsPage(prev => prev + 1)}
                          disabled={!reportsPagination.hasNextPage}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Review Report Modal */}
      {showReviewModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Review Report</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Report Type</label>
                <p className="p-3 bg-gray-50 rounded-lg capitalize">
                  {selectedReport.reportType.replace(/_/g, " ")}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <p className="p-3 bg-gray-50 rounded-lg">{selectedReport.description}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Decision</label>
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleReviewReportComplete(selectedReport.id, "confirmed", "Report confirmed after review")}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm Violation
                  </Button>
                  <Button
                    onClick={() => handleReviewReportComplete(selectedReport.id, "rejected", "Report rejected after review")}
                    variant="outline"
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Report
                  </Button>
                  <Button
                    onClick={() => handleReviewReportComplete(selectedReport.id, "investigating", "Under investigation")}
                    variant="outline"
                    className="flex-1"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Investigate
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowReviewModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalMonitoring;

